<template>
  <div class="mobile-remote-sheet-field">
    <div
      class="mobile-remote-trigger"
      :class="{ 'yellow lighten-5': isActiveSearch }"
    >
      <button type="button" class="mobile-remote-trigger-main" @click="openSheet">
        <span>{{ displayValue }}</span>
      </button>
      <button
        type="button"
        class="mobile-remote-trigger-icon"
        :title="isActiveSearch ? 'Reset filter' : 'Search filter'"
        @click="isActiveSearch ? handleDefault() : openSheet()"
      >
        <i class="material-icons black-text">{{ isActiveSearch ? "cancel" : "filter_list" }}</i>
      </button>
    </div>

    <div class="mobile-remote-backdrop" v-if="isOpen" @click="closeSheet"></div>
    <div class="mobile-remote-sheet" v-if="isOpen">
      <div class="mobile-remote-sheet-handle"></div>
      <div class="mobile-remote-sheet-head">
        <div>
          <label class="active teal-text">{{ sheetLabel }}</label>
          <h6>{{ sheetLabel }}</h6>
        </div>
        <button type="button" class="mobile-remote-close" @click="closeSheet">
          <i class="material-icons">close</i>
        </button>
      </div>

      <div class="mobile-remote-search-row">
        <i class="material-icons">search</i>
        <input
          ref="searchInput"
          type="text"
          :id="`${searchId}-sheet-search`"
          :name="`${searchId}-sheet-search`"
          v-model="searchItem"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          :placeholder="`Search ${sheetLabel.toLowerCase()}`"
          @beforeinput="handleDeferredTyping"
          @input="handleTyping"
          @keyup="handleTyping"
          @compositionupdate="handleDeferredTyping"
          @compositionend="handleTyping"
          @change="handleTyping"
        />
      </div>

      <div class="mobile-remote-progress" v-if="isSearching">
        <div class="progress">
          <div class="indeterminate"></div>
        </div>
      </div>

      <div class="mobile-remote-results">
        <button type="button" class="mobile-remote-option is-default" @click="handleDefault">
          {{ defaultItem }}
        </button>
        <button
          type="button"
          class="mobile-remote-option"
          v-for="item in resultItems"
          :key="item"
          @click="handleSelected(item)"
        >
          {{ item }}
        </button>
        <div class="mobile-remote-empty" v-if="showEmpty">No matches found</div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

export default {
  emits: ["selected", "searching", "clear"],
  props: ["inputId", "data", "startAgain", "label", "resetSignal"],
  setup(props, context) {
    const searchId = ref(props.inputId);
    const searchInput = ref(null);
    const searchItem = ref("");
    const displayValue = ref(Object.keys(props.data)[0]);
    const isOpen = ref(false);
    const isSearching = ref(false);
    const hasSearched = ref(false);
    const searchTimer = ref(null);

    const sheetLabel = computed(() => props.label || "Search");
    const allItems = computed(() => Object.keys(props.data || {}));
    const defaultItem = computed(() => allItems.value[0] || displayValue.value || "");
    const resultItems = computed(() => {
      if (!searchItem.value) {
        return [];
      }

      const search = String(searchItem.value).toLowerCase();

      return allItems.value.filter((x) => {
        return x !== defaultItem.value && String(x).toLowerCase().includes(search);
      });
    });
    const isActiveSearch = computed(() => displayValue.value && displayValue.value !== defaultItem.value);
    const showEmpty = computed(() => hasSearched.value && searchItem.value && !isSearching.value && !resultItems.value.length);

    const clearTimer = () => {
      if (searchTimer.value) {
        clearTimeout(searchTimer.value);
        searchTimer.value = null;
      }
    };

    const openSheet = () => {
      isOpen.value = true;
      searchItem.value = "";
      hasSearched.value = false;
      isSearching.value = false;

      nextTick(() => {
        if (searchInput.value) {
          searchInput.value.focus();
        }
      });
    };

    const closeSheet = () => {
      clearTimer();
      isOpen.value = false;
      isSearching.value = false;
      hasSearched.value = false;
      searchItem.value = "";
    };

    const requestSearch = (value) => {
      clearTimer();

      if (!value) {
        isSearching.value = false;
        hasSearched.value = false;
        return;
      }

      isSearching.value = true;
      hasSearched.value = true;
      searchTimer.value = setTimeout(() => {
        context.emit("searching", {
          id: searchId.value,
          value: value,
        });
      }, 280);
    };

    const handleTyping = (event) => {
      const value = event?.target?.value || searchItem.value;
      searchItem.value = value;
      requestSearch(value);
    };

    const handleDeferredTyping = (event) => {
      setTimeout(() => {
        handleTyping(event);
      }, 0);
    };

    const handleDefault = () => {
      clearTimer();
      displayValue.value = defaultItem.value;
      context.emit("selected", { id: searchId.value, value: displayValue.value, special: true });
      closeSheet();
    };

    const handleSelected = (val) => {
      clearTimer();
      displayValue.value = val;
      context.emit("selected", { id: searchId.value, value: val, special: false });
      closeSheet();
    };

    watch(
      () => props.data,
      () => {
        isSearching.value = false;
      }
    );

    watch(
      () => props.startAgain,
      (newVal) => {
        if (newVal) {
          displayValue.value = newVal;
          context.emit("clear", {
            id: searchId.value,
            value: null,
          });
        }
      }
    );

    watch(
      () => props.resetSignal,
      () => {
        if (props.startAgain) {
          displayValue.value = props.startAgain;
        } else {
          displayValue.value = defaultItem.value;
        }
      }
    );

    watch(defaultItem, (newVal) => {
      if (!displayValue.value) {
        displayValue.value = newVal;
      }
    });

    onBeforeUnmount(() => {
      clearTimer();
    });

    return {
      closeSheet,
      defaultItem,
      displayValue,
      handleDefault,
      handleDeferredTyping,
      handleSelected,
      handleTyping,
      isActiveSearch,
      isOpen,
      isSearching,
      openSheet,
      resultItems,
      searchId,
      searchInput,
      searchItem,
      sheetLabel,
      showEmpty,
    };
  },
};
</script>

<style scoped>
.mobile-remote-sheet-field {
  background: transparent;
  min-height: 52px;
}

.mobile-remote-trigger {
  align-items: center;
  background: transparent;
  border: 0;
  color: #263238;
  display: flex;
  font-size: 1.02rem;
  font-weight: 700;
  height: 52px;
  justify-content: space-between;
  line-height: 52px;
  margin: 0;
  padding: 0 10px 0 16px;
  text-align: left;
  width: 100%;
}

.mobile-remote-trigger-main {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  flex: 1 1 auto;
  font: inherit;
  height: 52px;
  line-height: 52px;
  min-width: 0;
  padding: 0;
  text-align: left;
}

.mobile-remote-trigger-main span {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-remote-trigger-icon {
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 52px;
  justify-content: center;
  padding: 0;
  width: 32px;
}

.mobile-remote-trigger-icon i {
  color: #00897b !important;
  height: 52px;
  line-height: 52px;
}

.mobile-remote-backdrop {
  background: transparent;
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 9996;
}

.mobile-remote-backdrop::before {
  background: rgba(38, 50, 56, 0.38);
  border-radius: 18px;
  bottom: 8px;
  content: "";
  left: 10px;
  position: absolute;
  right: 10px;
  top: 84px;
}

.mobile-remote-sheet {
  background: #ffffff;
  border: 1px solid #c7d3d8;
  border-radius: 16px 16px 0 0;
  bottom: 0;
  box-shadow: 0 -18px 34px -22px rgba(38, 50, 56, 0.62);
  left: 0;
  max-height: 82vh;
  overflow: hidden;
  padding: 10px 18px 18px;
  position: fixed;
  right: 0;
  z-index: 9997;
}

.mobile-remote-sheet-handle {
  background: #cfd8dc;
  border-radius: 999px;
  height: 4px;
  margin: 0 auto 14px;
  width: 44px;
}

.mobile-remote-sheet-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.mobile-remote-sheet-head h6 {
  color: #263238;
  font-size: 1.12rem;
  font-weight: 700;
  margin: 2px 0 0;
}

.mobile-remote-close {
  align-items: center;
  background: #f7fafb;
  border: 1px solid #d8e3e7;
  border-radius: 999px;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.mobile-remote-search-row {
  align-items: center;
  border: 1px solid #c7d3d8;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  min-height: 52px;
  padding: 0 14px;
}

.mobile-remote-search-row i {
  color: #00897b;
  flex: 0 0 auto;
}

.mobile-remote-search-row input {
  border: 0 !important;
  box-shadow: none !important;
  color: #263238;
  flex: 1 1 auto;
  font-size: 1.04rem;
  font-weight: 700;
  height: 50px !important;
  margin: 0 !important;
  min-width: 0;
}

.mobile-remote-search-row input:focus {
  border: 0 !important;
  box-shadow: none !important;
}

.mobile-remote-progress .progress {
  background: #e0f2f1;
  border-radius: 0;
  height: 2px;
  margin: 0;
}

.mobile-remote-results {
  border: 1px solid #e4ecef;
  border-radius: 6px;
  margin-top: 12px;
  max-height: calc(82vh - 156px);
  overflow-y: auto;
}

.mobile-remote-option {
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #eef3f5;
  color: #00897b;
  cursor: pointer;
  display: block;
  font-size: 1rem;
  line-height: 1.35;
  padding: 14px 16px;
  text-align: left;
  width: 100%;
}

.mobile-remote-option.is-default {
  color: #263238;
  font-weight: 700;
}

.mobile-remote-option:active,
.mobile-remote-option:focus {
  background: #e0f2f1;
  outline: none;
}

.mobile-remote-empty {
  color: #90a4ae;
  font-size: 0.94rem;
  line-height: 1.35;
  padding: 14px 16px;
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .mobile-remote-backdrop::before {
    border-radius: 20px;
    bottom: 24px;
    left: 24px;
    right: 24px;
    top: 112px;
  }

  .mobile-remote-sheet {
    border-radius: 10px;
    bottom: auto;
    left: 50%;
    max-width: 620px;
    right: auto;
    top: 50%;
    transform: translate(-50%, -50%);
    width: calc(100vw - 48px);
  }
}
</style>
